// /src/api/loaders.ts
import type {
  TimePeriod,
  NewsletterStats,
  SEOMetrics,
  AnalyticsOverview,
  Campaign,
  SubscriberHistory,
  MarketingMeta,
  SourceMeta,
  SourceState,
  LoadFailure,
} from '../types/marketing';

const API_BASE = '/api';

/* KEINE ATTRAPPENDATEN MEHR — AUCH NICHT ALS RUECKFALL.
 *
 * Hier standen bis zum 11.09.2026 rund 110 Zeilen erfundener Kennzahlen:
 * 2 847 Abonnenten, 18 420 Sitzungen, eine Kampagne „Winter Sale 2025" und ein
 * Abonnentenverlauf aus `Math.random()`. Sie sprangen ein, sobald die API nicht
 * antwortete, und fuellten `campaigns` und `subscriberHistory` sogar dann auf,
 * wenn sie antwortete. Die einzige Unterscheidung war die Plakette „Sample data"
 * an der Kachel — wer sie uebersah, las erfundene Zahlen als Messwerte.
 *
 * Jetzt gilt EINE Regel: eine Kennzahl wird nur uebernommen, wenn ihre Quelle
 * `live` meldet. Alles andere wird `null` bzw. eine leere Liste, und die Kachel
 * sagt, was fehlt.
 *
 * Die Regel steht bewusst HIER und nicht nur im Worker. `deploy.yml` liefert bei
 * einem Push auf `main` das Frontend aus, den Worker aber nicht — und die
 * ausgelieferte Worker-Fassung schickt fuer `demo` weiterhin Attrappen mit. Ohne
 * diesen Filter stuenden sie nach dem naechsten Frontend-Deploy wieder da, nur
 * ohne Plakette. */

export interface MarketingData {
  /** `null`, solange die Quelle nicht `live` meldet — nie eine Attrappe. */
  newsletter: NewsletterStats | null;
  seo: SEOMetrics | null;
  analytics: AnalyticsOverview | null;
  /** Leer, solange die Quelle nicht `live` meldet. */
  campaigns: Campaign[];
  subscriberHistory: SubscriberHistory[];
  /* `configured` ist entfallen. Es trug die Plakette „Live/Demo" an der
     Ueberschrift und war `false`, sobald EINE Quelle fehlte — mit vier
     angebundenen Quellen und nur Brevo ohne Schluessel stand „Demo" ueber
     echten Zahlen. Die Herkunft steht je Kachel in `meta`. */
  /** Zeitpunkt der Antwort. `null`, wenn keine kam. */
  lastUpdated: string | null;
  error: string | null;
  period: TimePeriod;
  /** Herkunft je Abschnitt, so wie der Worker sie meldet. */
  meta: MarketingMeta;
  /** Gesetzt, wenn ueberhaupt nichts geladen werden konnte. */
  failure: LoadFailure | null;
}

const everySource = (source: SourceState, reason: string): MarketingMeta => ({
  newsletter: { source, reason },
  seo: { source, reason },
  analytics: { source, reason },
  campaigns: { source, reason },
  subscriberHistory: { source, reason },
});

const liveOnly = <T>(meta: SourceMeta, value: T | null | undefined): T | null =>
  meta.source === 'live' && value != null ? value : null;

const liveList = <T>(meta: SourceMeta, value: unknown): T[] =>
  meta.source === 'live' && Array.isArray(value) ? (value as T[]) : [];

export const marketingLoader = async ({ request }: { request: Request }): Promise<MarketingData> => {
  const url = new URL(request.url);
  const period = (url.searchParams.get('period') as TimePeriod) || '30d';

  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const res = await fetch(`${origin}${API_BASE}/stats/overview?period=${period}`, {
      credentials: 'include',
    });

    // Ein 401 ist etwas anderes als ein 503, und beides etwas anderes als ein
    // abgerissenes Netz. Die alte Fassung warf fuer alle drei denselben Fehler
    // und schrieb „Using demo data. API returned 401" — der Satz, der den
    // eigentlichen Ausfall vier Monate lang zugedeckt hat.
    if (!res.ok) {
      const failure: LoadFailure =
        res.status === 401
          ? { kind: 'unauthenticated' }
          : res.status === 503
            ? { kind: 'auth_unavailable', detail: await describeFailure(res) }
            : { kind: 'unreachable', detail: `HTTP ${res.status}` };

      return nothingLoaded(period, failure);
    }

    const data = await res.json();

    // Meldet der Worker keine (oder eine unvollstaendige) Herkunft, gilt der
    // fehlende Teil als NICHT geladen — `unavailable`, nicht `demo`: ob die
    // Quelle angebunden ist, sagt uns in dem Fall niemand.
    const meta: MarketingMeta = {
      ...everySource('unavailable', 'The service does not report where these figures came from'),
      ...((data.meta as Partial<MarketingMeta> | undefined) ?? {}),
    };

    return {
      newsletter: liveOnly(meta.newsletter, data.newsletter as NewsletterStats | null),
      seo: liveOnly(meta.seo, data.seo as SEOMetrics | null),
      analytics: liveOnly(meta.analytics, data.analytics as AnalyticsOverview | null),
      campaigns: liveList<Campaign>(meta.campaigns, data.campaigns),
      subscriberHistory: liveList<SubscriberHistory>(meta.subscriberHistory, data.subscriberHistory),
      lastUpdated: data.lastUpdated ?? null,
      error: null,
      period,
      meta,
      failure: null,
    };
  } catch (err) {
    const failure: LoadFailure = {
      kind: 'unreachable',
      detail: err instanceof Error ? err.message : String(err),
    };
    console.warn('[marketShark] Kennzahlen nicht erreichbar:', err);
    return nothingLoaded(period, failure);
  }
};

/** Der Worker legt bei 503 einen Grund bei (auth_service_unavailable + Detail). */
async function describeFailure(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { reason?: string; detail?: string; error?: string };
    return body.detail || body.reason || body.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export function failureText(failure: LoadFailure): string {
  switch (failure.kind) {
    case 'unauthenticated':
      return 'Your session is not valid — please sign in again.';
    case 'auth_unavailable':
      return `The sign-in service is not responding: ${failure.detail}`;
    case 'unreachable':
      return `The marketing API is unreachable: ${failure.detail}`;
  }
}

/**
 * Die Antwort, wenn die Sammelroute gar nicht erst geantwortet hat.
 *
 * Jede Quelle steht dann auf `unavailable` und NICHT auf `demo`: die Anfrage kam
 * nie bis zu ihr, ob sie angebunden ist, weiss hier niemand. „Not connected" an
 * jeder Kachel waere die alte Verwechslung von „nicht angemeldet" und „nie
 * verkabelt" in neuer Form. Der Grund steht einmal oben in der Seitenmeldung
 * und noch einmal an jeder Kachel.
 */
function nothingLoaded(period: TimePeriod, failure: LoadFailure): MarketingData {
  const error = failureText(failure);
  return {
    newsletter: null,
    seo: null,
    analytics: null,
    campaigns: [],
    subscriberHistory: [],
    lastUpdated: null,
    error,
    period,
    meta: everySource('unavailable', error),
    failure,
  };
}
